import { TableColumnsType } from 'antd';
import { RevxMoney } from 'components/RevxMoney';
import RevxTable from 'components/RevxTable';
import {
  JobRFQShippingItemViewer,
  ShippingAccountTypeEnum,
} from 'domains/jobconfigDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { JobItem, JobParticipantEnum } from 'domains/workspaceDomain';
import { useEffect, useState } from 'react';
import { addressTranslator } from 'utils/address';
import { INTERNAL_DISTRIBUTION, SHIPPING_WILL_CALL } from 'utils/constants';
import { formatNumberToThousandths } from 'utils/number';
import './ShippingMethodTable.less';

type Props = {
  isSourcer: boolean;
  shippingItems: Array<JobRFQShippingItemViewer>;
  quantity: number;
  quantityIndex: number;
  orgId: number;
  qtySerialNumber: number;
  generateJobOrgId?: number;
  supplierName?: string;
  supplierCompanyName?: string;
  jobData: JobItem;
};

interface ShippingColumnsType extends JobRFQShippingItemViewer {
  serialNumber: number;
  address: string;
  methods: string;
  arrivalDateStr: string;
  readyDateStr: string;
  costs: number;
}

const ShippingMethodTable = ({
  shippingItems,
  quantity,
  quantityIndex,
  supplierCompanyName,
  supplierName,
  jobData,
}: Props) => {
  const [resource, setResource] = useState<ShippingColumnsType[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const shippingTitle =
    shippingItems.length > 1 ? 'Split Shipping' : 'Shipping';

  useEffect(() => {
    if (shippingItems) {
      const data: ShippingColumnsType[] = shippingItems.map((i, id) => {
        return formatShippingItemToTableData(i);
      });
      setResource(data);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingItems]);

  const formatShippingItemToTableData = (
    data: JobRFQShippingItemViewer
  ): ShippingColumnsType => {
    const { shippingInfo, deliveryDate, revxFreightCost, readyDate } = data;
    return {
      address: addressTranslator.dataToAddress(shippingInfo),
      methods: `${shippingInfo.carrierService} - ${
        shippingInfo.businessDaysInTransit
      } days - $${formatNumberToThousandths(revxFreightCost, {
        minimumFractionDigits: 2,
      })}`,
      arrivalDateStr: deliveryDate,
      readyDateStr: readyDate,
      costs: revxFreightCost,
      ...data,
    };
  };

  const columns: TableColumnsType<ShippingColumnsType> = [
    {
      title: 'Shipping Address',
      dataIndex: 'address',
      width: 240,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      width: 100,
      render: (v) => {
        return formatNumberToThousandths(v);
      },
    },
    {
      title: 'Shipment Method',
      dataIndex: 'methods',
      width: 260,
    },
    {
      title: 'Shipment account',
      dataIndex: 'account',
      render: (v, { shippingInfo: { accountCompany, carrierService } }) => {
        if (
          [SHIPPING_WILL_CALL, INTERNAL_DISTRIBUTION].includes(carrierService)
        ) {
          return <span>N/A</span>;
        }
        return accountCompany;
      },
    },
    {
      title: 'Ready Date',
      dataIndex: 'readyDateStr',
      width: 100,
    },
    {
      title: 'Arrival Date',
      dataIndex: 'arrivalDateStr',
      width: 100,
    },
    {
      title: 'Costs',
      dataIndex: 'costs',
      width: 100,
      render: (v, record) => {
        const { carrierType, accountType } = record;
        const isSpecial =
          carrierType === CarrierTypeEnum.UPS &&
          accountType === ShippingAccountTypeEnum.ORGANIZATION &&
          !(
            jobData?.participants?.length === 1 &&
            jobData?.participants.includes(JobParticipantEnum.PRODUCER)
          );
        return (
          <div>
            <RevxMoney value={v} />
            {isSpecial && '(billed separately)'}
          </div>
        );
      },
    },
  ];

  return (
    <div className="common-shipping-method-container">
      <div className="top-bar">
        <div className="top-bar__radio"></div>
        <div className="top-bar__split-info">
          {shippingTitle} for Quantity {quantityIndex + 1} :{' '}
          {formatNumberToThousandths(quantity)}
        </div>
      </div>
      <div className="table-content">
        <RevxTable<ShippingColumnsType>
          loading={loading}
          dataSource={resource}
          columns={columns}
          pagination={false}
          rowKey="serialNumber"
        />
      </div>
    </div>
  );
};

export default ShippingMethodTable;
